/**
 * The closed-episode lock as the chart reads it (EMR-1859).
 *
 * The backend decides whether a period is closed (`episode_closed` on a visit
 * or task row, `is_closed` on an episode) and refuses every clinical write on
 * one unless the actor holds `episode:amend-closed`. The chart only mirrors
 * that answer so the form renders read-only and the reason is on screen
 * before a save is refused. Hiding is UX; the backend is the gate.
 */
import { useStore } from '../store';
import { PermissionSlug } from '../constants/featureFlags';

/** True when the period is closed and this actor holds no key. */
export function isClosedEpisodeLocked(
  episodeClosed: boolean | null | undefined,
  canAmend: boolean
): boolean {
  return Boolean(episodeClosed) && !canAmend;
}

/** The chart-register sentences for a closed period. */
export function closedEpisodeCopy(canAmend: boolean): {
  headline: string;
  detail: string;
} {
  return canAmend
    ? {
        headline: 'This episode is closed.',
        detail: 'Your edits are logged as amendments.',
      }
    : {
        headline: 'This episode is closed.',
        detail: 'Amending it needs permission.',
      };
}

/**
 * Whether the signed-in user may amend a closed period.
 *
 * Reads the permission array itself, the way `QidTag` does, so the answer
 * re-evaluates when a grant reaches an open tab and the hook works under any
 * store shape that carries a user. A super user holds every key.
 */
export function useCanAmendClosedEpisode(): boolean {
  return useStore(state => {
    const permissions: string[] = state.user?.permissions ?? [];
    return (
      permissions.includes('admin:super-user') ||
      permissions.includes(PermissionSlug.EPISODE_AMEND_CLOSED)
    );
  });
}

/** `{ locked, canAmend }` for a visit or task row's `episode_closed`. */
export function useClosedEpisodeLock(
  episodeClosed: boolean | null | undefined
): { locked: boolean; canAmend: boolean } {
  const canAmend = useCanAmendClosedEpisode();
  return { locked: isClosedEpisodeLocked(episodeClosed, canAmend), canAmend };
}
